import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Button, Modal, Form } from 'react-bootstrap';
import Select from 'react-select';
import { Formik, Field } from 'formik';

import {
  counselorAssignments as counselorsDucks,
  users as usersDuck
} from 'ducks';
import { duckify, filterDuck } from 'utils/duckify';
import Placeholder from 'components/Placeholder';
import Utility from '../../../../legacy/helpers/shared/utility';
import Routes from '../../../../routes';
import './styles/BulkManageCounselors.scss';

const normalizeResponseData = (data) => data[0];

const BulkManageCounselors = ({
  className,
  contactIds,
  handleClose,
  saveCounselors,
  allDucksReady,
  counselors,
  getAssignedCounselors
}) => {
  const [saving, setSaving] = useState(false);
  const [assignedCounselors, setAssignedCounselors] = useState([]);
  const [initialValues, setInitialValues] = useState({
    assign: true,
    counselors: []
  });

  useEffect(() => {
    if (contactIds.length > 0) {
      getAssignedCounselors(contactIds)
        .then(response => {
          if (response && response.counselors) {
            setAssignedCounselors(response.counselors);
            setInitialValues({
              assign: true,
              counselors: response.counselors
            });
          }
        })
        .catch(error => {
          console.error('Error loading assigned counselors:', error);
        });
    }
  }, [contactIds, getAssignedCounselors]);

  const functionName = (values, formikBag) => {
    const data = {
      assign: values.assign,
      contact_ids: contactIds,
      user_ids: values.counselors.map(counselor => counselor.id)
    };
    setSaving(true);
    saveCounselors(data)
      .then(res => {
        Utility.successMessage(res.message);
        // Wait a little bit and then reload the page to refresh contact list data.
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      })
      .catch(res => {
        if (res.error) {
          Utility.errorMessage("We're sorry, an error has occurred. If the problem persists, please contact support.");
        }
      })
      .then(() => {
        setSaving(false);
        formikBag.setSubmitting(false);
        handleClose();
      });
  };

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize={true}
      validate={values => {
        const errors = {};
        if (values.counselors.length === 0) {
          errors.counselors = 'You must select at least 1 counselor';
        }

        return errors;
      }}
      onSubmit={functionName}
    >
      {({
        values,
        errors,
        setFieldValue,
        handleSubmit
      }) => (
        <Modal
          show
          className={className}
          animation={false}
          backdrop='static'
          keyboard={false}
        >
          <Modal.Header>
            <Modal.Title>Manage Counselors</Modal.Title>
          </Modal.Header>
          <Modal.Body className='manage-counselors-modal-body'>
            <Placeholder.loading ready={allDucksReady}>
              <p>
                Assign or remove counselors for&nbsp;
                <strong>{contactIds.length}</strong>
                &nbsp;selected contacts.
              </p>
              <Form noValidate className='manage-counselors-modal__form'>
                <Form.Group className='manage-counselors-modal__form-group'>
                  <Form.Label className='manage-counselors-modal__label'>Action</Form.Label>
                  <div className='manage-counselors-modal__control'>
                    <Field
                      as={Form.Check}
                      id='action-assign'
                      type='radio'
                      label='Assign'
                      name='assign'
                      value
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFieldValue('assign', true);
                          setFieldValue('counselors', []);
                        }
                      }}
                    />
                    <Field
                      as={Form.Check}
                      id='action-remove'
                      type='radio'
                      label='Remove'
                      name='assign'
                      value={false}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFieldValue('assign', false);
                          setFieldValue('counselors', assignedCounselors);
                        }
                      }}
                    />
                  </div>
                </Form.Group>
                <Form.Group className='manage-counselors-modal__form-group'>
                  <Form.Label className='manage-counselors-modal__label'>Counselors</Form.Label>
                  <div className='manage-counselors-modal__select'>
                    <Field
                      as={Select}
                      removeSelected={false}
                      closeOnSelect={false}
                      className={errors.counselors ? 'is-invalid' : ''}
                      multi
                      placeholder='Select counselors'
                      noResultsText='No counselors found'
                      options={counselors}
                      labelKey='name'
                      valueKey='id'
                      value={values.counselors}
                      onChange={selected => setFieldValue('counselors', selected)}
                    />
                    <Form.Control.Feedback type='invalid'>{errors.counselors}</Form.Control.Feedback>
                  </div>
                </Form.Group>
              </Form>
              {initialValues.assign && (
                <p className='text-muted'>
                  An email notification will be sent to assigned counselors.
                </p>
              )}
              {counselors.length === 0 && (
                <p className='error'>
                  Counselors cannot be assigned at this time. One or more users must be given the&nbsp;
                  <em>counselor</em>
                  &nbsp;permission in&nbsp;
                  <a href={Routes.admins_path()}>
                    Admin Accounts
                  </a>
                  &nbsp;under setup.
                </p>
              )}
            </Placeholder.loading>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant='light'
              onClick={handleClose}
            >
              Close
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </Formik>
  );
};

BulkManageCounselors.propTypes = {
  /** className prop */
  className: PropTypes.string,

  /** ID List of currently selected contacts */
  contactIds: PropTypes.arrayOf(PropTypes.string),

  /** allDucksReady bool */
  allDucksReady: PropTypes.bool.isRequired,

  /** counselor assignments duck */
  counselors: PropTypes.arrayOf(
    PropTypes.shape({})
  ).isRequired,

  /** saveCounselors POST api call to update counselors */
  saveCounselors: PropTypes.func.isRequired,

  /** getAssignedCounselors GET api call to get assigned counselors */
  getAssignedCounselors: PropTypes.func.isRequired,

  /** handleClose callback function */
  handleClose: PropTypes.func.isRequired
};

BulkManageCounselors.defaultProps = {
  className: '',
  contactIds: []
};

const mapDucksToProps = () => ({
  counselors: filterDuck(usersDuck, { counselor_permitted: 1 })
});

const mapDispatchToProps = dispatch => ({
  saveCounselors: data => dispatch(counselorsDucks.actions.manage(data))
    .then(counselorsDucks.actions.denormalizeObjects)
    .then(normalizeResponseData),
  getAssignedCounselors: contactIds => dispatch(counselorsDucks.actions.getAssignedCounselors(contactIds))
    .then(counselorsDucks.actions.denormalizeObjects)
});

export default duckify(mapDucksToProps, () => {}, mapDispatchToProps)(BulkManageCounselors);
